import { Grid, Card } from '@design-systems/canvas-ui'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import {
    DepartmentComparison,
    DepartmentSalesComparisons,
} from 'types/insights'
import { FC } from 'react'
import {
    addHiddenSpaceAfterSlashForWrapping,
    formatPercentChange,
    stringToTitleCase,
} from '../utils'
import './category-performance.scss'
import { NoData } from '../no-data/no-data'

interface CategoryPerformanceProps {
    data?: DepartmentSalesComparisons | null
}
export const CategoryPerformance: FC<CategoryPerformanceProps> = ({ data }) => {
    const { t } = useEssentials()
    const { insights } = TRANSLATION_KEYS

    return (
        <>
            <h2 className="hc8-mb-3x hc8-fs-250">
                {t(insights.categoryPerformance.heading)}
            </h2>
            <Card className="hc8-pa-3x">
                {!data ||
                (!data?.strong_vs_district_last_year?.length &&
                    !data?.weak_vs_district_last_year?.length) ? (
                    <NoData />
                ) : (
                    <Grid.Container
                        direction="row"
                        noWrap={true}
                        align="start"
                        spacing="dense"
                    >
                        <Grid.Item size={{ xs: 6 }}>
                            <ContextualizedSection
                                imagePath="/insights/Strong_Performance.svg"
                                heading={t(
                                    insights.categoryPerformance
                                        .strongVsDistrict,
                                )}
                                data={data?.strong_vs_district_last_year ?? []}
                            />
                        </Grid.Item>
                        <Grid.Item size={{ xs: 6 }}>
                            <ContextualizedSection
                                imagePath="/insights/Weak_Performance.svg"
                                heading={t(
                                    insights.categoryPerformance.weakVsDistrict,
                                )}
                                data={data?.weak_vs_district_last_year ?? []}
                            />
                        </Grid.Item>
                    </Grid.Container>
                )}
            </Card>
        </>
    )
}

interface ContextualizedSectionProps {
    imagePath: string
    heading: string
    data: DepartmentComparison[]
}

const ContextualizedSection: FC<ContextualizedSectionProps> = ({
    heading,
    imagePath,
    data,
}) => {
    return (
        <Grid.Container direction="column" align="start">
            <Grid.Item>
                <Grid.Container direction="row" noWrap align="center">
                    <Grid.Item>
                        <img
                            src={imagePath}
                            alt=""
                            aria-hidden="true"
                            className="contextualized-sales-image"
                        />
                    </Grid.Item>
                    <Grid.Item>
                        <strong className="hc8-clr-base hc8-fs-100">
                            {heading}
                        </strong>
                    </Grid.Item>
                </Grid.Container>
            </Grid.Item>
            <Grid.Item>
                <Grid.Container direction="column">
                    {data.map((row) => {
                        const isNegative =
                            row.district_sales_comp_gap_percent &&
                            row.district_sales_comp_gap_percent < 0
                        return (
                            <Grid.Item key={row.name}>
                                <Grid.Container
                                    direction="row"
                                    noWrap
                                    spacing="dense"
                                    justify="space-between"
                                    className="hc8-clr-base hc8-fs-050"
                                >
                                    <Grid.Item size={{ xs: 7 }}>
                                        {stringToTitleCase(
                                            addHiddenSpaceAfterSlashForWrapping(
                                                row.name,
                                            ),
                                        ) ?? '-'}
                                    </Grid.Item>
                                    <Grid.Item
                                        size={{ xs: 5 }}
                                        className={
                                            isNegative
                                                ? 'hc8-clr-error'
                                                : 'hc8-clr-success'
                                        }
                                    >
                                        <strong>
                                            {formatPercentChange(
                                                row.district_sales_comp_gap_percent,
                                            )}
                                        </strong>
                                    </Grid.Item>
                                </Grid.Container>
                            </Grid.Item>
                        )
                    })}
                </Grid.Container>
            </Grid.Item>
        </Grid.Container>
    )
}
